import { useEffect, useId, useRef, useState } from 'react'
import { Mtds, formatMatrixAmount, type MatrixAmount } from 'prove-common'
import { MatrixAmountFields } from '../matrix-amount-fields/MatrixAmountFields'
import {
  parseMatrixAmountDraft,
  toMatrixAmountDraft
} from '../../utils/matrixAmount'

type EditableMatrixAmountFieldProps = {
  value?: MatrixAmount | null
  canEdit: boolean
  isSaving?: boolean
  onSave: (
    amount: MatrixAmount | null
  ) => boolean | void | Promise<boolean | void>
}

export const EditableMatrixAmountField = ({
  value,
  canEdit,
  isSaving = false,
  onSave
}: EditableMatrixAmountFieldProps) => {
  const popoverId = useId()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [draft, setDraft] = useState(toMatrixAmountDraft(value))
  const hasAmount = value != null
  const parsed = parseMatrixAmountDraft(draft)
  const { amount } = parsed
  const error =
    parsed.error ??
    (hasAmount && amount == null
      ? 'Mengde kan ikke fjernes når den er satt'
      : undefined)
  const display = formatMatrixAmount(value) ?? '-'

  useEffect(() => {
    setDraft(toMatrixAmountDraft(value))
  }, [value])

  const reset = () => setDraft(toMatrixAmountDraft(value))

  const save = async () => {
    if (error) return
    const saved = await onSave(amount)
    if (saved === false) return
    closeButtonRef.current?.click()
  }

  if (!canEdit) return <>{display}</>

  return (
    <>
      <Mtds.Button
        type='button'
        data-variant='tertiary'
        popoverTarget={popoverId}
        disabled={isSaving}
        aria-label={`Rediger mengde prøvemateriale: ${hasAmount ? display : 'ikke satt'}`}
        className='-mx-1 h-auto min-h-0 min-w-0 justify-start font-normal [--mtdsc-button-padding:var(--mtds-1)]'
      >
        {display}
      </Mtds.Button>
      <Mtds.Popover id={popoverId} className='w-80 p-3'>
        <div className='grid gap-3'>
          <MatrixAmountFields
            draft={draft}
            error={error}
            disabled={isSaving}
            required={hasAmount}
            onChange={setDraft}
            onEnter={() => void save()}
          />
          <Mtds.Flex data-gap='2'>
            <Mtds.Button
              ref={closeButtonRef}
              type='button'
              popoverTarget={popoverId}
              popoverTargetAction='hide'
              hidden
            />
            <Mtds.Button
              type='button'
              popoverTarget={popoverId}
              popoverTargetAction='hide'
              disabled={isSaving}
              onClick={reset}
            >
              Avbryt
            </Mtds.Button>
            <Mtds.Button
              type='button'
              data-variant='primary'
              disabled={error != null || isSaving}
              onClick={() => void save()}
            >
              Lagre
            </Mtds.Button>
          </Mtds.Flex>
        </div>
      </Mtds.Popover>
    </>
  )
}
